import { groupSections, groupSummary, type QuoteGroup } from '@app/shared/price-calc';
import type { Columns } from './QuoteLineRow';
import { categoryLabels, type Category } from '../catalog/ItemForm';
import { bpToPct, type Section, type Version } from './types';

export type PartSummary = { label: string; grosze: number };
export type QuotePart = { group: QuoteGroup; sections: Section[]; summary: PartSummary[] };

/**
 * The quote on screen like template 2 and the PDF (F24b): equipment sections with "Łącznie urządzenia" and, with a
 * discount, "Łącznie po rabacie (25%)", then services with "Łącznie usługi". The same calculation as the PDF
 * (`groupSummary`); only sums of the saved lines, the discount stays on every line. Totals only for a role that
 * sees prices, discounts and amounts. A part with no sections is left out (an empty quote keeps its one part).
 */
export function quoteParts(version: Pick<Version, 'dayPctsBp' | 'sections'>, cols: Columns): QuotePart[] {
  const groups = groupSections(version.sections);
  const out: QuotePart[] = [];
  for (const group of ['equipment', 'services'] as const) {
    const sections = groups[group];
    if (sections.length === 0) continue;
    const lines = sections.flatMap((s) => s.lines);
    let summary: PartSummary[] = [];
    if (cols.price && cols.discount && cols.net && lines.length > 0) {
      try {
        const sum = groupSummary(
          lines.map((l) => ({ unitPriceGrosze: l.unitPriceGrosze ?? 0, quantityMilli: Math.round(l.quantity * 1000), daysMilli: l.daysMilli, linear: l.linear, discountBp: l.discountBp ?? 0, netGrosze: l.netGrosze ?? 0 })),
          version.dayPctsBp,
        );
        summary =
          group === 'services'
            ? [{ label: 'Łącznie usługi', grosze: sum.netGrosze }]
            : [
                { label: 'Łącznie urządzenia', grosze: sum.beforeDiscountGrosze },
                ...(sum.discounted ? [{ label: sum.commonDiscountBp !== null ? `Łącznie po rabacie (${bpToPct(sum.commonDiscountBp)}%)` : 'Łącznie po rabatach', grosze: sum.netGrosze }] : []),
              ];
      } catch {
        summary = []; // data beyond the limit: no part totals (the version totals say so already)
      }
    }
    out.push({ group, sections, summary });
  }
  return out;
}

/** P8: the fixed sections "Obsługa" and "Transport" are always in the editor; a frozen version shows them only filled. */
export function shownSections(version: Pick<Version, 'sections'>, editable: boolean): Section[] {
  return version.sections.filter((s) => editable || s.kind === 'equipment' || s.lines.length > 0);
}

/** The sum of a fixed section's lines (its own total, P8), or null when empty or the role does not see amounts. */
export function sectionSum(section: Section, cols: Columns): number | null {
  if (!cols.net || section.lines.length === 0) return null;
  return section.lines.reduce((sum, l) => sum + (l.netGrosze ?? 0), 0);
}

/**
 * P8 (point 9): the column "Rabat %" is shown only once some line or the version ("Rabat na urządzenia" in "ⓘ Info", formerly over the
 * table, also the default of new equipment lines) has a discount.
 */
/** Owner 2026-10-21: the "Dostawca" column only once the version has a line from a supplier (seen with the costs only). */
export function supplierShown(version: Pick<Version, 'sections'>): boolean {
  // owner 21:05: also once an own line is rented for the event (its suppliers listed)
  return version.sections.some((s) => s.lines.some((l) => !!l.supplierId || (l.rentals?.length ?? 0) > 0));
}

export function discountShown(version: Pick<Version, 'sections' | 'discountBp'>): boolean {
  return (version.discountBp ?? 0) > 0 || version.sections.some((s) => s.lines.some((l) => (l.discountBp ?? 0) > 0));
}

/**
 * P8 (point 10): what "+ Sekcja" suggests — the catalog's categories in its order (as the server sends them: a parent,
 * then its children), each once: those the version has a section of already are left out. `label` shows the path.
 */
export function sectionSuggestions(categories: Category[], sections: ReadonlyArray<Pick<Section, 'categoryId'>>): Array<{ id: string; name: string; label: string }> {
  const used = new Set(sections.map((s) => s.categoryId).filter((id): id is string => !!id));
  const names = new Map(categories.map((c) => [c.id, c.name]));
  return categoryLabels(categories)
    .filter((c) => !used.has(c.id))
    .map((c) => ({ id: c.id, name: names.get(c.id)!, label: c.label }));
}
